import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { formatQty, invalidateStockControl } from './stock-control-common';
import { BATCH_STATUSES, type BatchStatus, type BatchStatusResult, type BlockedBatch } from './stock-control-types';

export const BATCH_STATUS_LABELS: Record<BatchStatus, string> = {
  quarantined: 'Quarantine: hold it back from sale, issue and transfer',
  recalled: 'Recall: withdrawn by the maker or a regulator',
  active: 'Release: return it to normal stock',
};

export function BatchStatusDialog({ preset, onClose }: { preset: BlockedBatch; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<BatchStatus>('active');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () =>
      post<BatchStatusResult>('/pharmacy/stock/batches/status', {
        stockBatchIds: [preset.id],
        status,
        reason: reason.trim(),
      }),
    onSuccess: (result) => {
      if (result.changed === 0) {
        toast.warning(tr('pharmacy', 'nothing_changed'), `${result.batches} batch${result.batches === 1 ? ' was' : 'es were'} already set to ${humanize(result.status, 'batch_status')}.`);
      } else {
        toast.success(
          `${result.changed} batch${result.changed === 1 ? '' : 'es'} set to ${humanize(result.status, 'batch_status')}`,
          result.status === 'active' ? tr('pharmacy', 'back_in_normal_stock') : tr('pharmacy', 'it_can_no_longer_be_sold_issued_or_transferred'),
        );
      }
      void invalidateStockControl(queryClient);
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const missing: string[] = [];
  if (reason.trim().length < 3) missing.push('a reason of 3 or more characters');

  const title = `${preset.product.genericName} ${preset.product.strength ?? ''}`.trim();
  const detail = `Batch ${preset.batchNo} · ${preset.store.name} · ${formatQty(preset.qtyOnHand)} on hand`;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Release a batch"
      description={tr('pharmacy', 'blocked_stock_is_not_sold_issued_or_transferred')}
      footer={
        <>
          {missing.length > 0 && (
            <span className="me-auto self-center text-xs text-muted-foreground">{tr('pharmacy', 'still_needed')} {missing.join(', ')}</span>
          )}
          <Button variant="ghost" onClick={onClose} disabled={change.isPending}>
            {tr('pharmacy', 'cancel')}
          </Button>
          <Button
            variant={status === 'active' ? 'default' : 'destructive'}
            onClick={() => {
              setError(null);
              change.mutate();
            }}
            loading={change.isPending}
            disabled={missing.length > 0}
          >
            {status === 'active' ? tr('pharmacy', 'release') : status === 'recalled' ? tr('pharmacy', 'recall') : tr('pharmacy', 'quarantine')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md border px-3 py-2 text-sm">
          <p className="font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{detail}</p>
        </div>
        <Field label={tr('pharmacy', 'action')} required>
          <ChoiceButtons
            layout="cards"
            className="sm:grid-cols-1"
            value={status}
            onChange={setStatus}
            options={BATCH_STATUSES.map((option) => ({ value: option, label: BATCH_STATUS_LABELS[option] }))}
          />
        </Field>
        <Field label={tr('pharmacy', 'reason')} required hint={tr('pharmacy', 'kept_on_the_batch_history_at_least_3_characters')}>
          <Textarea rows={2} maxLength={300} value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
